import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiRequest } from 'utils/api';

// ==============================|| INITIAL STATE ||============================== //

interface AIProvider {
  id: string;
  code: string;
  displayName: string;
  description: string | null;
  status: string;
  displayOrder: number;
}

interface AIModel {
  id: string;
  code: string;
  displayName: string;
  provider: AIProvider;
  description: string | null;
  costTier: '$' | '$$' | '$$$';
  freeTierAllowed: boolean;
}

interface AIModelsState {
  providers: AIProvider[];
  models: AIModel[];
  status: 'idle' | 'loading' | 'success' | 'failed';
  error: string;
}

const initialState: AIModelsState = {
  providers: [],
  models: [],
  status: 'idle',
  error: '',
};

// ==============================|| ASYNC THUNKS ||============================== //

export const getAIProviders = createAsyncThunk(
  'aiModels/getProviders',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiRequest('/ai-models/providers');
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getAIModels = createAsyncThunk(
  'aiModels/getModels',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiRequest('/ai-models/models');
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// ==============================|| SLICE ||============================== //

const aiModelsSlice = createSlice({
  name: 'aiModels',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      // Get AI Providers
      .addCase(getAIProviders.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getAIProviders.fulfilled, (state, action) => {
        state.status = 'success';
        state.providers = action.payload.response || action.payload;
      })
      .addCase(getAIProviders.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      })

      // Get AI Models
      .addCase(getAIModels.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getAIModels.fulfilled, (state, action) => {
        state.status = 'success';
        state.models = action.payload.response || action.payload;
      })
      .addCase(getAIModels.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      });
  },
});

// ==============================|| EXPORTS ||============================== //

export const { clearError } = aiModelsSlice.actions;

export const selectAIProviders = (state: any) => state.aiModels.providers;
export const selectAIModels = (state: any) => state.aiModels.models;
export const selectAIModelsStatus = (state: any) => state.aiModels.status;
export const selectAIModelsError = (state: any) => state.aiModels.error;

export default aiModelsSlice.reducer;
